'use client';

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { Pause, Play } from 'react-feather';
import { publicAssetUrl } from '@wilkins-software/redwood-utils';
import {
  RIGGED_DUELIST_CLIPS,
  RIGGED_DUELIST_SOURCE_FPS,
  RIGGED_DUELIST_VARIANTS,
  riggedDuelistSheetPath,
  type RiggedDuelistClip,
} from './rigged-duelist-clips.data';
import {
  formatCellBackgroundPosition,
  resolveFrameIndexAtTime,
} from './rigged-duelist-playback.util';

/**
 * Interactive sprite-sheet player for the rigged-duelist article.
 *
 * Three things here are deliberate and look like mistakes:
 *
 * 1. **The sheets are CSS `background-image`, not the shared `Image`.** Every
 *    other raster on this site goes through Cloudflare Image Transformations,
 *    which resize per request. A resized sprite sheet is a broken one — each
 *    frame is addressed by an exact `background-position` offset in source
 *    pixels, so a re-encode at a different width silently shifts every cell.
 *    These sheets must ship byte-for-byte, and background images are not
 *    rewritten by the transform proxy.
 * 2. **Playback writes to the DOM directly, not through React state.** A frame
 *    index in `useState` re-renders the subtree sixty times a second per clip.
 *    Each clip owns one `requestAnimationFrame` loop that mutates
 *    `style.backgroundPosition` on its own refs, which stays smooth with
 *    thirteen clips on screen.
 * 3. **The rate control resamples rather than slowing playback down.** Picking
 *    24 fps keeps the clip's real duration and shows fewer distinct frames
 *    inside it. That is the honest comparison: the clips are *sampled* from
 *    keyframe curves, so the rate is a viewing choice, not a property of the
 *    art.
 */

const PLAYBACK_RATES = [24, 30, 60] as const;

/** Side of the inspection viewer, in CSS pixels. */
const LARGE_VIEWER_SIZE = 168;

/** Side of the viewer that matches the real in-game sprite slot. */
const BATTLE_VIEWER_SIZE = 112;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function subscribeToReducedMotion(onChange: () => void) {
  const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  mediaQuery.addEventListener('change', onChange);
  return () => mediaQuery.removeEventListener('change', onChange);
}

function getReducedMotionSnapshot(): boolean {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * The worker renders this markup too, where `matchMedia` does not exist.
 * Assuming motion is fine server-side keeps the markup identical for every
 * reader; the real preference is applied on hydration.
 */
function getReducedMotionServerSnapshot(): boolean {
  return false;
}

export type RiggedDuelistStageProps = {
  /** Comma-separated clip ids. Defaults to every clip, in authoring order. */
  clips?: string;
  /** Body variant selected on first paint. */
  defaultVariant?: string;
  /** Drop the body-variant control for sections that are not about damage. */
  showVariantControl?: boolean;
  /** Short line under the controls saying what to look at. */
  caption?: string;
};

function resolveClips(requested: string | undefined): RiggedDuelistClip[] {
  if (!requested) {
    return RIGGED_DUELIST_CLIPS;
  }

  const requestedIds = requested
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

  const resolved: RiggedDuelistClip[] = [];
  for (const requestedId of requestedIds) {
    const match = RIGGED_DUELIST_CLIPS.find((clip) => clip.id === requestedId);
    if (match) {
      resolved.push(match);
    }
  }

  return resolved.length > 0 ? resolved : RIGGED_DUELIST_CLIPS;
}

function formatSeconds(frames: number): string {
  return `${(frames / RIGGED_DUELIST_SOURCE_FPS).toFixed(2)} s`;
}

export function RiggedDuelistStage({
  clips,
  defaultVariant,
  showVariantControl = true,
  caption,
}: RiggedDuelistStageProps) {
  const stageId = useId();
  const visibleClips = useMemo(() => resolveClips(clips), [clips]);

  const [variantId, setVariantId] = useState(
    () => defaultVariant ?? RIGGED_DUELIST_VARIANTS[0].id,
  );
  const [playbackFps, setPlaybackFps] = useState<number>(60);

  const prefersReducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot,
  );

  // Null means "the reader has not chosen", so the motion preference decides.
  const [playbackOverride, setPlaybackOverride] = useState<boolean | null>(null);
  const isPlaying = playbackOverride ?? !prefersReducedMotion;

  const pausePlayback = useCallback(() => setPlaybackOverride(false), []);

  const activeVariantLabel =
    RIGGED_DUELIST_VARIANTS.find((variant) => variant.id === variantId)?.label ??
    variantId;

  return (
    <div className="not-prose my-8 overflow-hidden rounded-xl border border-[#b38e57]/40 bg-black">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-[#b38e57]/25 bg-[#432611]/25 px-4 py-3">
        {showVariantControl ? (
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="font-mono text-[10px] tracking-[0.14em] text-[#a89b88] uppercase"
              id={`${stageId}-body-label`}
            >
              Body
            </span>
            <div
              className="flex flex-wrap gap-1.5"
              role="group"
              aria-labelledby={`${stageId}-body-label`}
            >
              {RIGGED_DUELIST_VARIANTS.map((variant) => (
                <StageButton
                  key={variant.id}
                  isActive={variant.id === variantId}
                  onClick={() => setVariantId(variant.id)}
                >
                  {variant.label}
                </StageButton>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <span
            className="font-mono text-[10px] tracking-[0.14em] text-[#a89b88] uppercase"
            id={`${stageId}-rate-label`}
          >
            Rate
          </span>
          <div
            className="flex flex-wrap gap-1.5"
            role="group"
            aria-labelledby={`${stageId}-rate-label`}
          >
            {PLAYBACK_RATES.map((rate) => (
              <StageButton
                key={rate}
                isActive={rate === playbackFps}
                onClick={() => setPlaybackFps(rate)}
              >
                {rate} fps
              </StageButton>
            ))}
          </div>
        </div>

        <StageButton
          isActive={false}
          onClick={() => setPlaybackOverride(!isPlaying)}
        >
          {isPlaying ? (
            <Pause className="size-3" aria-hidden />
          ) : (
            <Play className="size-3" aria-hidden />
          )}
          {isPlaying ? 'Pause' : 'Play'}
        </StageButton>

        <span className="sr-only" aria-live="polite">
          {`Body variant ${activeVariantLabel}, ${playbackFps} frames per second, ${isPlaying ? 'playing' : 'paused'}.`}
        </span>
      </div>

      {caption ? (
        <p className="border-b border-[#b38e57]/25 px-4 py-3 text-sm leading-6 text-[#a89b88]">
          {caption}
        </p>
      ) : null}

      <div className="grid gap-px bg-[#b38e57]/20 sm:grid-cols-2">
        {visibleClips.map((clip) => (
          <ClipCard
            key={clip.id}
            clip={clip}
            variantId={variantId}
            playbackFps={playbackFps}
            isPlaying={isPlaying}
            onScrubStart={pausePlayback}
          />
        ))}
      </div>
    </div>
  );
}

type StageButtonProps = {
  isActive: boolean;
  onClick: () => void;
  children: ReactNode;
};

function StageButton({ isActive, onClick, children }: StageButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors',
        'focus-visible:ring-2 focus-visible:ring-[#f2d181]/60 focus-visible:outline-none',
        isActive
          ? 'border-[#b0853e] bg-[#432611] text-[#f2d181]'
          : 'border-[#b38e57]/30 bg-[#432611]/40 text-[#d4c4a8] hover:bg-[#432611]/80 hover:text-[#ede9e0]',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

type ClipCardProps = {
  clip: RiggedDuelistClip;
  variantId: string;
  playbackFps: number;
  isPlaying: boolean;
  onScrubStart: () => void;
};

function ClipCard({
  clip,
  variantId,
  playbackFps,
  isPlaying,
  onScrubStart,
}: ClipCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const largeRef = useRef<HTMLDivElement>(null);
  const battleRef = useRef<HTMLDivElement>(null);
  const scrubRef = useRef<HTMLInputElement>(null);
  const frameIndexRef = useRef(0);
  const isScrubbingRef = useRef(false);

  /** Flipped once the card is near the viewport, which is what fetches bytes. */
  const [hasBeenRevealed, setHasBeenRevealed] = useState(false);

  const paintFrame = useCallback(
    (frameIndex: number) => {
      if (largeRef.current) {
        largeRef.current.style.backgroundPosition = formatCellBackgroundPosition(
          { frameIndex, columns: clip.columns, viewerSize: LARGE_VIEWER_SIZE },
        );
      }
      if (battleRef.current) {
        battleRef.current.style.backgroundPosition =
          formatCellBackgroundPosition({
            frameIndex,
            columns: clip.columns,
            viewerSize: BATTLE_VIEWER_SIZE,
          });
      }
      if (scrubRef.current && !isScrubbingRef.current) {
        scrubRef.current.value = String(frameIndex);
      }
    },
    [clip.columns],
  );

  // Reveal the card the first time it comes near the viewport. Thirteen clips
  // across three body variants is 6.6 MB of sheets; a reader who stops half way
  // should pay for half.
  useEffect(() => {
    const cardElement = cardRef.current;
    if (!cardElement || hasBeenRevealed) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasBeenRevealed(true);
        }
      },
      { rootMargin: '320px 0px' },
    );
    observer.observe(cardElement);

    return () => observer.disconnect();
  }, [hasBeenRevealed]);

  /**
   * Point both viewers at the sheet for the current variant, once the bytes
   * have actually decoded. Swapping `background-image` before the decode lands
   * blanks the viewer for a frame or two, which reads as a limb dropping out —
   * precisely the defect the article says rigging removes.
   *
   * The frame index is untouched across a variant change on purpose: the claim
   * is that the same keyframes drive a damaged body, so the pose must not jump
   * when the body changes underneath it.
   */
  useEffect(() => {
    if (!hasBeenRevealed) {
      return;
    }

    let isStale = false;
    const sheetUrl = publicAssetUrl(
      riggedDuelistSheetPath(variantId, clip.id).replace(/^\//, ''),
    );

    const preloader = new window.Image();
    preloader.src = sheetUrl;
    preloader
      .decode()
      .then(() => {
        if (isStale) {
          return;
        }
        const viewers: [HTMLDivElement | null, number][] = [
          [largeRef.current, LARGE_VIEWER_SIZE],
          [battleRef.current, BATTLE_VIEWER_SIZE],
        ];
        for (const [element, size] of viewers) {
          if (!element) {
            continue;
          }
          element.style.backgroundImage = `url("${sheetUrl}")`;
          element.style.backgroundSize = `${clip.columns * size}px ${clip.rows * size}px`;
        }
        paintFrame(frameIndexRef.current);
      })
      .catch(() => {
        // A failed decode leaves the previous sheet in place, which is a better
        // outcome than an empty viewer.
      });

    return () => {
      isStale = true;
    };
  }, [hasBeenRevealed, variantId, clip, paintFrame]);

  // Frames are addressed by wall time, so dropping to 24 fps shows fewer
  // distinct frames over the same duration rather than playing in slow motion.
  // Playback resumes from the frame it was paused on rather than restarting.
  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    let animationFrameHandle = 0;
    let startedAt = 0;

    const tick = (timestamp: number) => {
      animationFrameHandle = requestAnimationFrame(tick);

      if (startedAt === 0) {
        startedAt =
          timestamp -
          (frameIndexRef.current / RIGGED_DUELIST_SOURCE_FPS) * 1000;
      }
      if (isScrubbingRef.current) {
        startedAt = 0;
        return;
      }

      const nextIndex = resolveFrameIndexAtTime({
        elapsedSeconds: (timestamp - startedAt) / 1000,
        frameCount: clip.frames,
        playbackFps,
      });

      if (nextIndex !== frameIndexRef.current) {
        frameIndexRef.current = nextIndex;
        paintFrame(nextIndex);
      }
    };

    animationFrameHandle = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameHandle);
  }, [isPlaying, playbackFps, clip.frames, paintFrame]);

  return (
    <div className="bg-black p-4" ref={cardRef}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="font-serif text-lg text-[#f2d181]">{clip.title}</h3>
        <span className="font-mono text-[10px] tracking-[0.1em] text-[#b0853e] uppercase">
          {`${clip.frames} frames · ${formatSeconds(clip.frames)} · ${clip.loops ? 'loops' : 'one-shot'}`}
        </span>
      </div>

      <div className="flex items-start gap-4">
        <div className="flex shrink-0 flex-col items-center gap-1.5">
          <div
            ref={largeRef}
            style={{ width: LARGE_VIEWER_SIZE, height: LARGE_VIEWER_SIZE }}
            className="rounded border border-[#b38e57]/25 bg-no-repeat"
            role="img"
            aria-label={`${clip.title}: ${clip.description}`}
          />
          <span className="font-mono text-[9px] tracking-[0.1em] text-[#a89b88] uppercase">
            {`${LARGE_VIEWER_SIZE} px`}
          </span>
        </div>

        <div className="flex shrink-0 flex-col items-center gap-1.5">
          <div className="rounded-lg border-2 border-[#b38e57] bg-black/80 p-1">
            <div
              ref={battleRef}
              style={{ width: BATTLE_VIEWER_SIZE, height: BATTLE_VIEWER_SIZE }}
              className="bg-no-repeat"
              aria-hidden
            />
          </div>
          <span className="font-mono text-[9px] tracking-[0.1em] text-[#a89b88] uppercase">
            In the real frame
          </span>
        </div>
      </div>

      <input
        type="range"
        min={0}
        max={clip.frames - 1}
        step={1}
        defaultValue={0}
        ref={scrubRef}
        aria-label={`${clip.title} frame`}
        className="mt-3 w-full accent-[#b0853e]"
        onPointerDown={() => {
          isScrubbingRef.current = true;
          onScrubStart();
        }}
        onPointerUp={() => {
          isScrubbingRef.current = false;
        }}
        onBlur={() => {
          isScrubbingRef.current = false;
        }}
        onChange={(event) => {
          const nextIndex = Number(event.currentTarget.value);
          frameIndexRef.current = nextIndex;
          paintFrame(nextIndex);
        }}
      />

      <p className="mt-2 text-sm leading-6 text-[#a89b88]">{clip.description}</p>
    </div>
  );
}
